import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCall } from "@/context/CallContext";
import { useSocket } from "@/context/SocketContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import GiftMenu from "@/components/GiftMenu";
import { GIFT_ICON, dollars } from "@/lib/giftIcons";
import { Phone, PhoneOff, Video, Gift, Target, Lock, AlertTriangle, VenetianMask, EyeOff, Camera } from "lucide-react";
import { AddPersonButton, IncomingInvite, ConsentCard } from "@/components/ThreeWay";
import { isPremium } from "@/lib/membership";
import { preloadFaceMask } from "@/lib/faceMask";

const FALLBACK = "https://images.unsplash.com/photo-1637059880830-59a90102de77";
const CONFETTI_COLORS = ["#D4AF37", "#8E2A47", "#34d399", "#f0ead6"];

function Celebration({ title }) {
  const particles = useMemo(() => [...Array(36)].map((_, i) => ({
    x: (Math.random() - 0.5) * 900,
    y: (Math.random() - 0.7) * 700,
    rotate: Math.random() * 360,
    duration: 2 + Math.random() * 1.2,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  })), []);
  return (
    <div data-testid="goal-celebration" className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center overflow-hidden">
      {particles.map((p, i) => (
        <motion.span key={i} initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{ x: p.x, y: p.y, opacity: 0, scale: 0.4, rotate: p.rotate }}
          transition={{ duration: p.duration, ease: "easeOut" }}
          className="absolute w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
      ))}
      <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        className="bg-black/60 backdrop-blur-md border border-gold/50 rounded-3xl px-10 py-6 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Goal reached</p>
        <p className="font-display text-3xl font-light mt-2">{title}</p>
        <p className="text-sm text-text-muted mt-1">Thank you, admirers</p>
      </motion.div>
    </div>
  );
}

function Elapsed({ since }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.max(0, Math.floor((now - since) / 1000));
  return <span data-testid="private-timer">{String(Math.floor(s / 60)).padStart(2, "0")}:{String(s % 60).padStart(2, "0")}</span>;
}

function GoalBar({ goal, label }) {
  const pct = Math.min(100, Math.round((goal.progress / goal.amount) * 100));
  return (
    <div data-testid="goal-bar" className="bg-black/50 backdrop-blur-md border border-gold/30 rounded-2xl px-4 py-2.5">
      <div className="flex items-center justify-between gap-4 text-[11px] uppercase tracking-[0.15em]">
        <span className="text-gold flex items-center gap-1.5 truncate"><Target size={12} /> {label}: {goal.title}</span>
        <span className={goal.reached ? "text-emerald-400" : "text-text-main/80"} data-testid="goal-progress-text">
          {goal.reached ? "Goal reached" : `${dollars(goal.progress)} / ${dollars(goal.amount)}`}
        </span>
      </div>
      <div className="h-1.5 mt-2 rounded-full bg-white/10 overflow-hidden">
        <motion.div initial={false} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }}
          className={`h-full rounded-full ${goal.reached ? "bg-emerald-400" : "bg-gold"}`} />
      </div>
    </div>
  );
}

function Stream({ stream, muted, className }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} />;
}

export default function CallModal() {
  const { status, peer, localStream, remoteStream, privateInfo, maskMode, setMaskMode, acceptCall, endCall, guests, isGuest } = useCall();
  const guestList = Object.entries(guests || {});
  const { addGiftListener, addGoalListener } = useSocket();
  const { user } = useAuth();
  const [giftOpen, setGiftOpen] = useState(false);
  const [floats, setFloats] = useState([]);
  const [goals, setGoals] = useState({});
  const [celebrate, setCelebrate] = useState(null);
  const [privateSince, setPrivateSince] = useState(null);

  useEffect(() => {
    if (privateInfo?.status === "active") setPrivateSince((s) => s || Date.now());
    else if (!privateInfo) setPrivateSince(null);
  }, [privateInfo]);

  // Warm up the face-mask model for Premium users so switching feels instant.
  useEffect(() => {
    if (isPremium(user) && (status === "active" || status === "calling" || status === "incoming")) {
      preloadFaceMask();
    }
  }, [status, user]);

  useEffect(() => {
    const off = addGiftListener((data) => {
      const id = Date.now() + Math.random();
      setFloats((f) => [...f, { id, gift: data.gift, name: data.from_name }]);
      setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 2600);
    });
    return off;
  }, [addGiftListener]);

  useEffect(() => {
    const off = addGoalListener((d) => {
      setGoals((g) => {
        const prev = g[d.user_id];
        if (d.goal?.reached && !(prev && prev.reached)) {
          setCelebrate({ title: d.goal.title, key: Date.now() });
          setTimeout(() => setCelebrate(null), 4500);
        }
        return { ...g, [d.user_id]: d.goal };
      });
    });
    return off;
  }, [addGoalListener]);

  useEffect(() => {
    if (status !== "active") return;
    const ids = [peer?.id, user?.id].filter(Boolean);
    ids.forEach((uid) => {
      api.get(`/gifts/goal/${uid}`).then(({ data }) => {
        if (data.goal) setGoals((g) => ({ ...g, [uid]: data.goal }));
      }).catch(() => {});
    });
  }, [status, peer?.id, user?.id]);

  if (status === "idle") return <IncomingInvite />;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="call-modal"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-ink/95 backdrop-blur-lg flex items-center justify-center px-0 sm:px-4"
      >
        {status === "active" ? (
          <div className="relative w-full h-full max-w-6xl mx-auto sm:py-8">
            <div className="relative w-full h-full sm:rounded-3xl overflow-hidden sm:border border-white/10 bg-black">
              <Stream stream={remoteStream} muted={false} className="w-full h-full object-cover" />
              {!remoteStream && (
                <div className="absolute inset-0 flex items-center justify-center text-text-muted font-display italic text-2xl">
                  Connecting video…
                </div>
              )}
              <ConsentCard />
              {guestList.map(([gid, g]) => (
                <div key={gid} data-testid={`guest-tile-${gid}`} className="absolute bottom-[7.5rem] left-3 w-24 h-36 sm:bottom-6 sm:left-6 sm:w-40 sm:h-56 rounded-2xl overflow-hidden border border-gold/40 shadow-2xl bg-black/60">
                  {g.stream ? <Stream stream={g.stream} muted={false} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-xs text-text-muted">Connecting…</div>}
                  <span className="absolute bottom-2 left-2 right-2 truncate text-[10px] uppercase tracking-[0.12em] text-gold bg-black/60 px-2 py-0.5 rounded-full">
                    {g.name || "Guest"}{g.billing ? ` · ${g.billing === "inviter" ? "on you" : "pays own"}` : ""}
                  </span>
                </div>
              ))}
              <div data-testid="local-video-tile" className="absolute bottom-[7.5rem] right-3 w-24 h-36 sm:bottom-6 sm:right-6 sm:w-40 sm:h-56 rounded-2xl overflow-hidden border border-white/20 shadow-2xl">
                <Stream stream={localStream} muted className="w-full h-full object-cover" />
                {maskMode !== "off" && (
                  <span data-testid="mask-active-badge" className="absolute top-2 left-2 flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-gold bg-black/60 px-2 py-0.5 rounded-full">
                    {maskMode === "mask" ? <VenetianMask size={10} /> : <EyeOff size={10} />} {maskMode === "mask" ? "Masked" : "Blurred"}
                  </span>
                )}
              </div>
              {isPremium(user) && (
                <div data-testid="face-mask-control" className="absolute bottom-[7.5rem] left-1/2 -translate-x-1/2 sm:translate-x-0 sm:bottom-6 sm:left-6 flex items-center gap-1 bg-black/50 backdrop-blur-md border border-white/15 rounded-full p-1">
                  <button data-testid="mask-off" onClick={() => setMaskMode("off")} title="No filter"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${maskMode === "off" ? "bg-white/20 text-white" : "text-text-muted hover:text-white"}`}>
                    <Camera size={16} />
                  </button>
                  <button data-testid="mask-mask" onClick={() => setMaskMode("mask")} title="Masquerade mask"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${maskMode === "mask" ? "bg-gold/25 text-gold" : "text-text-muted hover:text-gold"}`}>
                    <VenetianMask size={16} />
                  </button>
                  <button data-testid="mask-blur" onClick={() => setMaskMode("blur")} title="Blur my face"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${maskMode === "blur" ? "bg-gold/25 text-gold" : "text-text-muted hover:text-gold"}`}>
                    <EyeOff size={16} />
                  </button>
                </div>
              )}
              <div className="absolute top-[max(1rem,env(safe-area-inset-top))] left-3 sm:top-6 sm:left-6 max-w-[60%] truncate text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-text-main/80 bg-black/40 px-3 sm:px-4 py-2 rounded-full backdrop-blur-md">
                Live with {peer?.display_name}
              </div>
              {privateInfo && (
                <div data-testid="private-bar" className="absolute top-14 left-3 right-3 justify-between sm:justify-start sm:left-auto sm:top-6 sm:right-6 flex items-center gap-2 sm:gap-3 bg-black/50 backdrop-blur-md border border-wine/50 rounded-full px-4 sm:px-5 py-2.5 text-[10px] sm:text-xs uppercase tracking-[0.15em]">
                  <span className="text-gold flex items-center gap-1.5"><Lock size={12} /> Private</span>
                  {privateSince ? <Elapsed since={privateSince} /> : <span>--:--</span>}
                  <span data-testid="private-cost" className="text-gold">{dollars(privateInfo.total_charged || 0)}</span>
                  <span className="text-text-muted">{dollars(privateInfo.rate)}/min</span>
                </div>
              )}
              {privateInfo?.low_balance && privateInfo?.status === "active" && (
                <div data-testid="low-balance-warning" className="absolute top-[6.75rem] left-3 right-3 sm:left-auto sm:top-20 sm:right-6 flex items-center gap-2 bg-amber-500/15 backdrop-blur-md border border-amber-500/50 text-amber-300 rounded-full px-4 sm:px-5 py-2.5 text-[10px] sm:text-xs uppercase tracking-[0.15em]">
                  <AlertTriangle size={13} /> About 1 minute left — top up your wallet
                </div>
              )}
              {celebrate && <Celebration key={celebrate.key} title={celebrate.title} />}
              {/* Live gift goal bars */}
              <div className="absolute top-[6.5rem] sm:top-6 left-1/2 -translate-x-1/2 w-full max-w-md px-3 sm:px-4 space-y-2 z-10">
                {peer?.id && goals[peer.id] && <GoalBar goal={goals[peer.id]} label={`${peer.display_name}'s goal`} />}
                {user?.id && goals[user.id] && <GoalBar goal={goals[user.id]} label="Your goal" />}
              </div>
              {/* Floating gift animations */}
              <div className="absolute bottom-[17rem] sm:bottom-24 left-0 right-0 flex justify-center pointer-events-none">
                {floats.map((f) => {
                  const GI = GIFT_ICON[f.gift?.icon] || GIFT_ICON.heart;
                  return (
                    <div key={f.id} data-testid="gift-float" className="gift-float absolute flex flex-col items-center">
                      <span className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: `${f.gift?.color}33`, border: `1px solid ${f.gift?.color}` }}>
                        <GI size={30} style={{ color: f.gift?.color }} />
                      </span>
                      <span className="text-xs mt-1" style={{ color: f.gift?.color }}>{f.gift?.gift_name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="absolute bottom-[max(1.5rem,env(safe-area-inset-bottom))] sm:bottom-14 left-1/2 -translate-x-1/2 flex items-center gap-3 sm:gap-4">
              {privateInfo?.session_id && privateInfo.status === "active" && !isGuest && !user?.is_creator && <AddPersonButton rate={privateInfo.rate} />}
              <button data-testid="call-gift" onClick={() => setGiftOpen(true)}
                className="w-16 h-16 rounded-full border border-gold/50 text-gold hover:bg-gold/10 transition-colors flex items-center justify-center">
                <Gift size={22} />
              </button>
              <button data-testid="call-end" onClick={() => endCall(true)}
                className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center">
                <PhoneOff size={22} />
              </button>
            </div>
            <GiftMenu open={giftOpen} onClose={() => setGiftOpen(false)} toUserId={peer?.id} toName={peer?.display_name} context="live" />
          </div>
        ) : (
          <div className="text-center max-w-sm px-6">
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_50%_40%,rgba(114,47,55,0.6),transparent_60%)]" />
            <div className="relative z-10">
              <p className="text-xs uppercase tracking-[0.4em] text-gold mb-6 flex items-center justify-center gap-2">
                <Video size={14} /> {status === "calling" ? "Calling" : "Incoming video date"}
              </p>
              <motion.img
                animate={{ scale: [1, 1.04, 1] }} transition={{ repeat: Infinity, duration: 2 }}
                src={peer?.photo_url || FALLBACK} alt=""
                className={`w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-2 border-wine mx-auto mb-6 ${peer?.discreet_mode ? "blur-lg" : ""}`}
              />
              <h2 className="font-display text-3xl sm:text-4xl font-light mb-2">{peer?.display_name || "Member"}</h2>
              {privateInfo && (
                <p data-testid="incoming-private-rate" className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.2em] text-gold mb-3">
                  <Lock size={12} /> Private session · {dollars(privateInfo.rate)}/min
                </p>
              )}
              <p className="text-text-muted mb-10">
                {status === "calling" ? "Waiting for them to answer…" : "wants to connect live, camera to camera."}
              </p>
              <div className="flex items-center justify-center gap-6">
                {status === "incoming" && (
                  <button data-testid="call-accept" onClick={acceptCall}
                    className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 transition-colors flex items-center justify-center">
                    <Phone size={22} />
                  </button>
                )}
                <button data-testid="call-decline" onClick={() => endCall(true)}
                  className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center">
                  <PhoneOff size={22} />
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
